// The app's public, pre-shell routes -- declared ONCE here and consumed twice
// in src/App.js: to render the <Route> elements, and to gate the
// ensureUserAccount effect that provisions a signed-in user's account.
//
// Two review findings on Task 7 put this list here rather than in App.js.
//
// First, the gate used to be a Set of exact pathnames tested with
// `.has(location.pathname)`, but React Router's matcher is neither exact nor
// case-sensitive: /welcome/, /Welcome, and /WELCOME/ all render WelcomePage
// while missing an exact-string gate. Calling ensureUserAccount there would
// provision a default-tier workspace before the invitation is accepted --
// exactly the race the header comments on WelcomePage.js and
// InvitationShell.js exist to prevent. Matching with the router's own
// matchPath removes that discrepancy by construction: the predicate answers
// "is this route public?" the same way the router answers "which route
// renders?".
//
// Second, that Set duplicated the route table and had already drifted -- it
// omitted /portal/:token. The patterns and the <Route> elements now read from
// this one object, so they cannot disagree, and a public route added later is
// added in exactly one place.

import { matchPath } from 'react-router-dom';

/**
 * Every route registered above App.js's path="*" catch-all. Values are React
 * Router path PATTERNS (they may carry `:param` segments), not literal
 * pathnames.
 */
export const PUBLIC_ROUTE_PATHS = Object.freeze({
    portal: '/portal/:token',
    login: '/login',
    privacy: '/privacy',
    terms: '/terms',
    acceptInvite: '/accept-invite',
    welcome: '/welcome',
    // help and helpArticle (ART-03) are deliberately public: a support link
    // must open a help page before sign-in. Being in this object is what
    // keeps ensureUserAccount from firing for a signed-out reader.
    help: '/help',
    helpArticle: '/help/:slug',
    // signup (D-02) is the public plan-selection mount -- the stranger-facing
    // half of PlanSelection, the other half being the zero-workspace branch
    // in AppRouter.js. Because /signup is public, App.js's isPublicRoutePath
    // gate means ensureUserAccount will NOT fire here, so a signed-in user
    // with zero workspaces sitting on /signup cannot rely on the
    // accountStatus !== null gate AppRouter.js uses -- App.js's D-04 redirect
    // guard is what handles the signed-in case instead.
    signup: '/signup',
    // Phase 68 lead capture (D-14). Stranger-facing links: a signed-in agent
    // who opens one must not run ensureUserAccount (it would provision a
    // workspace), and a guest must never reach the app shell. Tokens are
    // case-sensitive and never lowercased anywhere on these routes.
    lead: '/lead/:token',
    openHouse: '/open-house/:token',
    kiosk: '/kiosk',
    confirmLead: '/confirm/:token',
});

export const PUBLIC_ROUTE_PATTERNS = Object.freeze(Object.values(PUBLIC_ROUTE_PATHS));

/**
 * True when `pathname` renders one of the public, pre-shell routes above.
 *
 * Pure and side-effect free so the gate can be unit-tested directly, without
 * mounting App (this repo's node:test runner hangs forever on a bare
 * `await act(async ...)` -- see the header of tests/welcomeTourLauncher.test.js).
 *
 * A non-string or empty pathname is NOT public: the account lookup failing
 * open into the shell is recoverable, whereas provisioning a workspace on an
 * invitation route is not.
 */
export function isPublicRoutePath(pathname) {
    if (typeof pathname !== 'string' || pathname === '') return false;
    return PUBLIC_ROUTE_PATTERNS.some(
        pattern => matchPath({ path: pattern, end: true, caseSensitive: false }, pathname) !== null
    );
}
